import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { updateLocalStateValues } from 'components/RabbitOKRSidePanel/KRslice';
import { useLocalState } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/useLocalState';
import { QUERY_KEYS } from 'components/RabbitOKRSidePanel/datahooks';
import { useDeleteOKRMutation } from 'components/RabbitOKRSidePanel/datahooks/useDeleteOKRMutation';
import { useOKRRealignMutation } from 'components/RabbitOKRSidePanel/datahooks/useOKRRealignMutation';
import { useOKRRealignViewData } from 'components/RabbitOKRSidePanel/datahooks/useOKRRealignViewData';
import {
  SECONDARY_PANEL_TYPES,
  closeRealignPanel,
  openChangeDetailsPanel,
  openRealignPanelWithoutChildrenRealignModal,
  openTitleAndMetricPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useOKRDrawerControlCallbacks } from 'components/RabbitOKRSidePanel/useOKRDrawerControlCallbacks';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';

import { OBJECTIVE_LEVEL, OKR_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getRouteAfterRemovingTaskAndObjectiveDetails, getTimePeriodIdString } from 'constants/helper';
import { sendOKRDeletedEvent, sendRealignedKREvent, sendRealignedObjectiveEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { RealignFooter } from './RealignFooter';
import { RealignGrid } from './RealignGrid';
import { RealignHeader } from './RealignHeader';

/* Currently this section is handling both OKR alignment and OKR's children realignment */
export const RealignSection = ({ isCollapse }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const history = useHistory();
  const toast = useToast();
  const page = useViewPageName();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { onDelete, onUpdate } = useOKRDrawerControlCallbacks();

  /* 
    read primary kr panel details, this will work but
    so far this component was relying only on secondaryPanel state
  */
  const { timeBoundDetails, localStateValues = {} } = useSelector(kRDrawerSelector);

  const { type, details } = useSelector(kRDrawerSecondaryPanelSelector);

  const { okrTimePeriod, okrUuid, okrType, objectiveLevel, parentUuid } = details;
  const isObjective = okrType === OKR_TYPES.OBJECTIVE;

  const isChildrenRealignPanel = type === SECONDARY_PANEL_TYPES.REALIGN_CHILDREN;
  const isDeletionChildrenRealignPanel = type === SECONDARY_PANEL_TYPES.REALIGN_CHILDREN_AND_DELETE;
  const isLeftTitleAndMetricSuggestionPanel =
    details?.parentState?.type === SECONDARY_PANEL_TYPES.TITLE_AND_METRICS_SUGGESTION_PANEL;
  const initialLocalTimePeriod = isLeftTitleAndMetricSuggestionPanel
    ? localStateValues?.suggestedOKRDetails[localStateValues?.currentCardIndex]?.timeBoundDetails
    : localStateValues?.realignApiPayload?.timeBoundPeriod;
  const [localSelectedTimePeriod, setLocalSelectedTimePeriod] = useLocalState(initialLocalTimePeriod || okrTimePeriod);
  const [selectedParentUuid, setSelectedParentUuid] = useLocalState(parentUuid);

  const gridRef = useRef();

  const handleExpandAll = () => {};

  const onCancel = useCallback(() => {
    if (details.isOpenAsChild) {
      if (details?.parentState?.type === SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL) {
        dispatch(openChangeDetailsPanel({}));
      } else if (isLeftTitleAndMetricSuggestionPanel) {
        dispatch(
          openTitleAndMetricPanel({
            isNewOKR: false,
            isOpenAsChild: true,
            leftPanelDetails: { type: SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL, details: {} },
            isAlignmentDisabled: false,
          })
        );
      }
    } else {
      // @ts-ignore
      dispatch(closeRealignPanel());
    }
  }, [details.isOpenAsChild, details?.parentState?.type, dispatch, isLeftTitleAndMetricSuggestionPanel]);

  useEffect(() => {
    if (isCollapse) {
      onCancel();
    }
  }, [isCollapse, onCancel]);

  const { data, isLoading } = useOKRRealignViewData({
    timeBoundPeriod: localSelectedTimePeriod,
  });

  const { mutate, isLoading: isMutating } = useOKRRealignMutation(okrUuid);
  const { mutate: deletionMutation, isLoading: isDeleting } = useDeleteOKRMutation(okrUuid);

  const [isRemovingAlignment, setIsRemovingAlignment] = useState(false);

  const onSave = async ({ selectedParentUuid, localSelectedTimePeriod, isChildrenRealignPanel }) => {
    if (details.isOpenAsChild && !isChildrenRealignPanel) {
      const realignApiPayload = {
        selectedParentUuid,
        timeBoundPeriod: localSelectedTimePeriod,
        performChildrenRealign: isChildrenRealignPanel,
      };
      const selectedParent = data?.filter((curParent) => curParent.uuid === selectedParentUuid)[0] || {};
      const { visibilityType, title, type: parentType } = selectedParent;

      const parentPost = selectedParentUuid
        ? {
            uuid: selectedParentUuid,
            text: title,
            type: parentType,
            visibilityType,
          }
        : {
            uuid: null,
            text: 'None',
            type: null,
            visibilityType: null,
          };
      if (details?.parentState?.type === SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL) {
        dispatch(
          updateLocalStateValues({
            parentPost,
            timeBoundDetails: localSelectedTimePeriod,

            realignApiPayload,
          })
        );
        dispatch(openChangeDetailsPanel());
        return;
      }
      if (details?.parentState?.type === SECONDARY_PANEL_TYPES.TITLE_AND_METRICS_SUGGESTION_PANEL) {
        const { suggestedOKRDetails, currentCardIndex } = localStateValues;
        const newSuggestedOKRDetails = cloneDeep(suggestedOKRDetails);
        newSuggestedOKRDetails[currentCardIndex].parentPost = parentPost;
        newSuggestedOKRDetails[currentCardIndex].timeBoundDetails = localSelectedTimePeriod;
        newSuggestedOKRDetails[currentCardIndex].realignApiPayload = realignApiPayload;
        console.log('newSuggestedOKRDetails', newSuggestedOKRDetails[currentCardIndex]);
        dispatch(updateLocalStateValues({ suggestedOKRDetails: newSuggestedOKRDetails }));
        dispatch(
          openTitleAndMetricPanel({
            isNewOKR: false,
            isOpenAsChild: true,
            leftPanelDetails: { type: SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL, details: {} },
            isAlignmentDisabled: false,
          })
        );
      }
      return;
    }

    /* Queries to invalidate
      1.1 OKR ( so that parent and timeline component reflects updated )
      1.2 OKR so that progress updates can reflect ( in case of derived progress)
      2. refetch alignment section.
    */
    const okrQueryKey = [QUERY_KEYS.KEY_RESULT, okrUuid];
    const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, okrUuid];
    try {
      mutate(
        {
          selectedParentUuid,
          timeBoundPeriod: localSelectedTimePeriod,
          performChildrenRealign: isChildrenRealignPanel,
        },
        {
          onError: (err) => {
            setIsRemovingAlignment(false);
            toast({
              title: err.message || 'Failed to realign',
              status: TOAST_STATUSES.ERROR,
              position: 'bottom-left',
            });
          },
          onSuccess: () => {
            queryClient.invalidateQueries(okrQueryKey);
            queryClient.invalidateQueries(alignmentQueryKey);

            if (okrType === OKR_TYPES.OBJECTIVE) {
              sendRealignedObjectiveEvent({
                eventProperties: {
                  goalId: okrUuid,
                  type: details?.eventType || 'Realign',
                  source: details?.source || 'Kebab Menu',
                  page,
                },
              });
            } else {
              sendRealignedKREvent({
                eventProperties: {
                  goalId: okrUuid,
                  type: details?.eventType,
                  source: details?.source || 'Kebab Menu',
                  page,
                },
              });
            }

            queryClient.removeQueries(REACT_QUERY_KEYS.okrTreeView);

            if (typeof onUpdate === 'function') {
              console.log('inside onUpdate');
              onUpdate(parentUuid, okrUuid);
            }
            if (isChildrenRealignPanel) {
              toast({
                title: `Child ${modifyString(
                  moduleName,
                  StringModifier.PLURAL_SENTENCE_CASE
                )} have been successfully re-aligned. You can now re-align your ${modifyString(
                  moduleName,
                  StringModifier.SENTENCE_CASE
                )}.`,
                status: TOAST_STATUSES.SUCCESS,
                position: 'bottom-left',
              });
              dispatch(openRealignPanelWithoutChildrenRealignModal(details));
            } else {
              toast({
                title: `The ${
                  okrType === OKR_TYPES.OBJECTIVE
                    ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                    : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                } has been successfully re-aligned`,
                status: TOAST_STATUSES.SUCCESS,
                position: 'bottom-left',
              });
              dispatch(closeRealignPanel());
            }

            setIsRemovingAlignment(false);
          },
        }
      );
    } catch (err) {
      setIsRemovingAlignment(false);
      console.error(err.message);
    }
  };

  const onRealign = () => onSave({ selectedParentUuid, localSelectedTimePeriod, isChildrenRealignPanel });

  const onRemoveAlignment = () => {
    setIsRemovingAlignment(true);
    onSave({
      selectedParentUuid: null,
      localSelectedTimePeriod,
      isChildrenRealignPanel: false,
    });
  };

  /* Following onRealign and Delete will only be called when opening realign panel from deletion modal */
  const onRealignAndDelete = async () => {
    /* Queries to invalidate
      1.1 OKR ( so that parent and timeline component reflects updated )
      1.2 OKR so that progress updates can reflect ( in case of derived progress)
      2. refetch alignment section.
    */
    try {
      mutate(
        {
          selectedParentUuid,
          timeBoundPeriod: localSelectedTimePeriod,
          performChildrenRealign: true,
        },
        {
          onError: (err) => {
            toast({
              title:
                err.message ||
                `Child ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)} could not be re-aligned`,
              status: TOAST_STATUSES.ERROR,
            });
          },
          onSuccess: () => {
            toast({
              title: `Child ${modifyString(
                moduleName,
                StringModifier.PLURAL_SENTENCE_CASE
              )} have been successfully re-aligned.`,
              status: TOAST_STATUSES.SUCCESS,
              position: 'bottom-left',
            });

            if (okrType === OKR_TYPES.OBJECTIVE) {
              sendRealignedObjectiveEvent({
                eventProperties: {
                  goalId: okrUuid,
                  type: details?.eventType || 'Realign',
                  source: details?.source || 'Kebab Menu',
                  page,
                },
              });
            } else {
              sendRealignedKREvent({
                eventProperties: {
                  goalId: okrUuid,
                  type: details?.eventType || 'Realign',
                  source: details?.source || 'Kebab Menu',
                  page,
                },
              });
            }

            const okrQueryKey = [QUERY_KEYS.KEY_RESULT, okrUuid];
            const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, okrUuid];
            const okrSupportedByQueryKey = [QUERY_KEYS.OKR_CHILDREN, okrUuid];

            const closeDrawer = () => {
              const route = getRouteAfterRemovingTaskAndObjectiveDetails({
                location: history.location,
              });
              history.push(route);
            };

            deletionMutation(
              {},
              {
                onError: (err) => {
                  toast({
                    title:
                      err.message ||
                      `The ${
                        isObjective
                          ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                          : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                      } could not be archived`,
                    status: TOAST_STATUSES.ERROR,
                    position: 'bottom-left',
                  });
                },
                onSuccess: () => {
                  queryClient.removeQueries(okrQueryKey);
                  queryClient.removeQueries(alignmentQueryKey);
                  queryClient.removeQueries(okrSupportedByQueryKey);

                  queryClient.removeQueries(REACT_QUERY_KEYS.okrTreeView);
                  sendOKRDeletedEvent({
                    eventProperties: {
                      goalId: okrUuid,
                      type: okrType === OKR_TYPES.OBJECTIVE ? OKR_TYPES.OBJECTIVE : OKR_TYPES.KEY_RESULT,
                      source: 'Kebab Menu',
                      page,
                      status: 'archive',
                    },
                  });

                  if (typeof onDelete === 'function') {
                    console.log('inside onDelete');
                    onDelete(parentUuid, okrUuid);
                  }

                  toast({
                    title: `The ${
                      okrType === OKR_TYPES.OBJECTIVE
                        ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                        : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
                    } has been successfully re-aligned`,
                    status: TOAST_STATUSES.SUCCESS,
                    position: 'bottom-left',
                  });

                  dispatch(closeRealignPanel());
                  closeDrawer();
                },
              }
            );
          },
        }
      );
    } catch (err) {
      console.error(err.message);
    }
  };

  /* 
    Following code is to handle remove alignment based on privileges
    Starts here
  */
  const { canCreateKeyResultAtRootLevel } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const cannotRealignKeyResultAtRoot = okrType !== OKR_TYPES.OBJECTIVE && !canCreateKeyResultAtRootLevel;

  const cannotRealignObjectiveAtRoot = okrType === OKR_TYPES.OBJECTIVE && objectiveLevel !== OBJECTIVE_LEVEL.COMPANY;

  /* 
    Above code is to handle access based on config and privileges.
    Ends here
  */

  const isSelectedTimelineSameAsCurrentOKRTimeline =
    getTimePeriodIdString(timeBoundDetails) === getTimePeriodIdString(localSelectedTimePeriod);

  const hideRealignAsRoot =
    isChildrenRealignPanel ||
    isDeletionChildrenRealignPanel ||
    !parentUuid ||
    cannotRealignKeyResultAtRoot ||
    cannotRealignObjectiveAtRoot ||
    !isSelectedTimelineSameAsCurrentOKRTimeline;

  const canRealignThisOKRAtRoot =
    okrType === OKR_TYPES.OBJECTIVE ? objectiveLevel === OBJECTIVE_LEVEL.COMPANY : canCreateKeyResultAtRootLevel;

  const showRealignInThisTimelineAsRoot =
    !isSelectedTimelineSameAsCurrentOKRTimeline &&
    !isChildrenRealignPanel &&
    !isDeletionChildrenRealignPanel &&
    canRealignThisOKRAtRoot;

  const handleRealignInThisTimelineAsRoot = onRemoveAlignment;

  return (
    <Flex flexDirection="column" flex={1}>
      <RealignHeader
        gridRef={gridRef}
        localSelectedTimePeriod={localSelectedTimePeriod}
        setLocalSelectedTimePeriod={setLocalSelectedTimePeriod}
        handleExpandAll={handleExpandAll}
      />

      <RealignGrid
        localSelectedTimePeriod={localSelectedTimePeriod}
        selectedParentUuid={selectedParentUuid}
        setSelectedParentUuid={setSelectedParentUuid}
        gridRef={gridRef}
        gridData={data}
      />
      <RealignFooter
        showRealignInThisTimelineAsRoot={showRealignInThisTimelineAsRoot}
        handleRealignInThisTimelineAsRoot={handleRealignInThisTimelineAsRoot}
        hideRealignAsRoot={hideRealignAsRoot}
        onCancel={onCancel}
        disableRealign={isLoading || selectedParentUuid === parentUuid}
        isMutating={isMutating || isDeleting}
        isRemovingAlignment={isRemovingAlignment}
        onRemoveAlignment={onRemoveAlignment}
        realignButtonText={isDeletionChildrenRealignPanel ? 'Re-align and Archive' : 'Re-align'}
        onRealign={isDeletionChildrenRealignPanel ? onRealignAndDelete : onRealign}
      />
    </Flex>
  );
};
